import React, { useEffect, useState } from 'react';
import { Building2, ShieldCheck, Hash, Phone, MapPin, Loader2, Clock, XCircle, Package, Briefcase, Send } from 'lucide-react';
import { driverApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PhotoDocPicker from './PhotoDocPicker';

const SERVICES = [
  { v: 'same_day_package', label: 'Same-day packages', icon: Package },
  { v: 'courier', label: 'Courier (signature)', icon: Briefcase },
  { v: 'business_logistics', label: 'Business logistics', icon: Building2 },
];

const EMPTY = {
  business_name: '', business_registration_number: '', business_registration_doc_url: '',
  business_insurance_provider: '', business_insurance_policy_number: '', business_insurance_doc_url: '',
  business_phone: '', business_address: '',
  business_services_offered: ['same_day_package', 'courier', 'business_logistics'],
};

const CourierApplicationPanel = ({ driver, onChange }) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    driverApi.courierApplication()
      .then((r) => {
        setApplication(r.application);
        if (r.application && r.application.status === 'rejected') {
          const a = r.application;
          setForm({
            business_name: a.business_name || '', business_registration_number: a.business_registration_number || '',
            business_registration_doc_url: a.business_registration_doc_url || '',
            business_insurance_provider: a.business_insurance_provider || '',
            business_insurance_policy_number: a.business_insurance_policy_number || '',
            business_insurance_doc_url: a.business_insurance_doc_url || '',
            business_phone: a.business_phone || '', business_address: a.business_address || '',
            business_services_offered: a.business_services_offered || EMPTY.business_services_offered,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    const required = [
      ['business_name', 'Business name'],
      ['business_registration_number', 'Registration number'],
      ['business_registration_doc_url', 'Business registration document'],
      ['business_insurance_provider', 'Insurance provider'],
      ['business_insurance_policy_number', 'Policy number'],
      ['business_insurance_doc_url', 'Insurance document'],
    ];
    for (const [k, label] of required) {
      if (!String(form[k] || '').trim()) { toast.error(`${label} is required`); return; }
    }
    setSubmitting(true);
    try {
      const app = await driverApi.submitCourierApplication(form);
      setApplication(app);
      setShowForm(false);
      toast.success('Application submitted — our team will review it shortly.');
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-4 border-t border-slate-100 flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const isCourier = driver?.is_courier;
  const pending = application?.status === 'pending';
  const rejected = application?.status === 'rejected';
  const formVisible = showForm || (!isCourier && !pending && !application);

  return (
    <div className="pt-4 border-t border-slate-100" data-testid="courier-application-panel">
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" /> COURIER application
            {isCourier && (
              <span className="text-[10px] uppercase tracking-wider bg-indigo-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="courier-status-approved">
                <ShieldCheck className="h-3 w-3" /> COURIER verified
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-lg">
            Submit your <b>business registration</b> and <b>insurance</b> documents for admin approval to earn the
            COURIER badge — required for <b>same-day packages</b>, <b>courier services</b> and <b>business logistics</b>.
          </p>
        </div>
      </div>

      {isCourier && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="courier-verified-banner">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          Your business is verified. You&rsquo;re eligible for same-day packages, courier and business-logistics orders.
        </div>
      )}

      {!isCourier && pending && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="courier-status-pending">
          <div className="flex items-center gap-2 font-medium"><Clock className="h-4 w-4 shrink-0" /> Application under review</div>
          <p className="text-xs mt-1 text-amber-700">
            Submitted {application.submitted_at ? new Date(application.submitted_at).toLocaleString() : ''} — our team is
            reviewing <b>{application.business_name}</b>. You&rsquo;ll be notified once a decision is made.
          </p>
        </div>
      )}

      {!isCourier && rejected && !showForm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800" data-testid="courier-status-rejected">
          <div className="flex items-center gap-2 font-medium"><XCircle className="h-4 w-4 shrink-0" /> Application not approved</div>
          <p className="text-xs mt-1 text-rose-700">Reason: {application.rejection_reason || 'Not specified'}</p>
          <Button size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setShowForm(true)} data-testid="courier-reapply-btn">
            Fix &amp; re-apply
          </Button>
        </div>
      )}

      {!isCourier && formVisible && (
        <div className="mt-3 space-y-4" data-testid="courier-application-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field icon={Building2} label="Company / business name *" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} testId="courier-app-business-name" />
            <Field icon={Hash} label="Registration number *" value={form.business_registration_number} onChange={(v) => setForm({ ...form, business_registration_number: v })} testId="courier-app-reg-number" />
            <Field icon={ShieldCheck} label="Insurance provider *" value={form.business_insurance_provider} onChange={(v) => setForm({ ...form, business_insurance_provider: v })} testId="courier-app-ins-provider" />
            <Field icon={ShieldCheck} label="Policy number *" value={form.business_insurance_policy_number} onChange={(v) => setForm({ ...form, business_insurance_policy_number: v })} testId="courier-app-policy-number" />
            <Field icon={Phone} label="Business phone" value={form.business_phone} onChange={(v) => setForm({ ...form, business_phone: v })} testId="courier-app-business-phone" />
            <Field icon={MapPin} label="Business address" value={form.business_address} onChange={(v) => setForm({ ...form, business_address: v })} testId="courier-app-business-address" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PhotoDocPicker
              label="Business registration document *"
              value={form.business_registration_doc_url}
              onChange={(url) => setForm((p) => ({ ...p, business_registration_doc_url: url }))}
              testIdPrefix="courier-app-reg-doc"
              cameraTitle="Business registration"
              compact
            />
            <PhotoDocPicker
              label="Business insurance document *"
              value={form.business_insurance_doc_url}
              onChange={(url) => setForm((p) => ({ ...p, business_insurance_doc_url: url }))}
              testIdPrefix="courier-app-ins-doc"
              cameraTitle="Business insurance"
              compact
            />
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-2">Services my business can perform</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SERVICES.map((s) => {
                const active = (form.business_services_offered || []).includes(s.v);
                return (
                  <button
                    type="button" key={s.v}
                    onClick={() => {
                      const cur = new Set(form.business_services_offered || []);
                      if (active) cur.delete(s.v); else cur.add(s.v);
                      setForm({ ...form, business_services_offered: Array.from(cur) });
                    }}
                    data-testid={`courier-app-svc-${s.v}`}
                    className={`h-12 rounded-xl border flex items-center gap-2 px-3 text-sm font-medium ${active ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  >
                    <s.icon className="h-4 w-4" />{s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button onClick={submit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="courier-app-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </Button>
          <p className="text-[10px] text-slate-400 -mt-1">An admin reviews every application. You&rsquo;ll be notified in-app and by email once a decision is made.</p>
        </div>
      )}
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative mt-1">
      {Icon && <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      <Input className="pl-9 h-10" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  </div>
);

export default CourierApplicationPanel;
